'use client';

import { useEffect, useState, type JSX } from 'react';
import { usePathname, useParams } from 'next/navigation';
import Link from 'next/link';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Loader } from '@/components/ui/loader';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useWalkthrough } from '@/contexts/walkthrough-context';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { CircleHelp, MessageSquare, ShieldCheck } from 'lucide-react';
import {
  getFloatingActionPillState,
  type FloatingActionPillPlacement,
} from '@/components/floating-action-pill-state';

/**
 * Single floating pill that fuses the "take a tour" affordance and the global
 * feedback action into one capsule. Each action is one half of the pill, sized
 * to the (smaller) tour-button scale; when only one of the two is available on
 * a given route the lone half renders as a clean circle.
 */

const ALLOWED_PATHS = [
  '/design',
  '/avatar',
  '/references',
  '/reference',
  '/branding',
  '/brandings',
  '/aesthetics',
  '/settings',
];

const STRICT_PATHS = new Set([
  '/avatar',
  '/references',
  '/reference',
  '/branding',
  '/brandings',
  '/aesthetics',
]);

interface FloatingActionPillProps {
  collapsed?: boolean;
  placement?: FloatingActionPillPlacement;
}

function useVisibleThreadSidebarPill(enabled: boolean): boolean {
  const [hasVisiblePill, setHasVisiblePill] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const update = () => {
      const visible = Array.from(
        document.querySelectorAll<HTMLElement>(
          '[data-floating-action-pill-placement="thread-sidebar"]'
        )
      ).some((element) => {
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);
        return (
          rect.width > 0 &&
          rect.height > 0 &&
          style.display !== 'none' &&
          style.visibility !== 'hidden'
        );
      });
      setHasVisiblePill(visible);
    };

    update();
    let updateFrame: number | null = null;
    const scheduleUpdate = () => {
      if (updateFrame !== null) return;
      updateFrame = window.requestAnimationFrame(() => {
        updateFrame = null;
        update();
      });
    };
    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'hidden', 'style'],
    });
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      if (updateFrame !== null) window.cancelAnimationFrame(updateFrame);
    };
  }, [enabled]);

  return hasVisiblePill;
}

export function FloatingActionPill({
  collapsed = false,
  placement = 'floating',
}: FloatingActionPillProps = {}): JSX.Element | null {
  const pathname = usePathname();
  const params = useParams();
  const orgSlug = params?.orgSlug as string | undefined;

  const { hasWalkthroughForRoute, isActive, start } = useWalkthrough();
  const t = useTranslations('GlobalUI');
  const workspaceShellT = useTranslations('WorkspaceShell');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isSidebarPlacement = placement === 'thread-sidebar';
  const hideBehindVisibleSidebarPill =
    useVisibleThreadSidebarPill(!isSidebarPlacement);

  const [feedback, setFeedback] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submitFeedback = useMutation(api.feedback.mutations.submitFeedback);

  const isFeedbackRoute = ALLOWED_PATHS.some((path) => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const orgPath = orgSlug ? `/${orgSlug}${normalizedPath}` : null;

    if (STRICT_PATHS.has(normalizedPath)) {
      return pathname === normalizedPath || pathname === orgPath;
    }

    return (
      pathname === normalizedPath ||
      pathname.startsWith(`${normalizedPath}/`) ||
      (orgPath !== null &&
        (pathname === orgPath || pathname.startsWith(`${orgPath}/`)))
    );
  });

  const { helpAction, showFeedback } = getFloatingActionPillState({
    placement,
    hasWalkthroughForRoute,
    isWalkthroughActive: isActive,
    isFeedbackRoute,
    hasWorkspace: Boolean(orgSlug),
  });

  if ((!helpAction && !showFeedback) || hideBehindVisibleSidebarPill) {
    return null;
  }

  const both = Boolean(helpAction) && showFeedback;
  const label =
    helpAction === 'tour' ? t('takeTour') : workspaceShellT('support');
  const hint = t('tourHint');

  const shouldStackAboveMobileFab =
    pathname === '/files/avatars' ||
    (orgSlug ? pathname === `/${orgSlug}/files/avatars` : false);

  const halfBase = isSidebarPlacement
    ? cn(
        'text-muted-foreground hover:bg-foreground/5 hover:text-foreground focus-visible:bg-foreground/5 focus-visible:text-foreground focus-visible:ring-ring/40 flex h-7 min-w-0 items-center justify-center gap-1.5 rounded-md px-1.5 text-[11px] font-medium transition-colors duration-150 outline-none focus-visible:ring-2',
        collapsed && 'size-7 px-0'
      )
    : 'group/half relative flex size-10 items-center justify-center text-muted-foreground transition-colors duration-150 hover:bg-accent hover:text-foreground focus:outline-none focus-visible:relative focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-primary/40';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await submitFeedback({
        feedback: feedback.trim(),
        pageUrl:
          typeof window !== 'undefined' ? window.location.href : undefined,
        orgSlug,
      });
      toast.success(t('feedbackSubmitted'));
      setFeedback('');
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setSubmitError(t('feedbackFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setSubmitError(null);
  };

  const helpControlClassName = cn(
    halfBase,
    !isSidebarPlacement && (both ? 'rounded-l-full' : 'rounded-full')
  );
  const helpControlContent = (
    <>
      <CircleHelp
        className={cn(
          'shrink-0',
          isSidebarPlacement
            ? 'size-3.5'
            : 'size-5 transition-transform duration-150 group-hover/half:scale-110'
        )}
      />
      {isSidebarPlacement && !collapsed ? <span>{label}</span> : null}
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <div
        data-floating-action-pill-placement={placement}
        className={cn(
          isSidebarPlacement
            ? 'pointer-events-auto w-full print:hidden'
            : 'fixed right-4 z-50 print:hidden',
          !isSidebarPlacement &&
            (shouldStackAboveMobileFab ? 'bottom-20 lg:bottom-4' : 'bottom-4')
        )}
      >
        <div
          className={cn(
            'flex items-center',
            isSidebarPlacement
              ? collapsed
                ? 'flex-col gap-1'
                : 'w-full justify-start gap-0.5 px-1'
              : 'border-border bg-background/90 rounded-full border shadow-lg backdrop-blur'
          )}
        >
          {helpAction ? (
            <Tooltip>
              <TooltipTrigger asChild>
                {helpAction === 'support' && orgSlug ? (
                  <Link
                    href={`/${orgSlug}/support`}
                    aria-label={label}
                    className={helpControlClassName}
                  >
                    {helpControlContent}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={start}
                    aria-label={label}
                    data-walkthrough-help-button
                    className={helpControlClassName}
                  >
                    {helpControlContent}
                  </button>
                )}
              </TooltipTrigger>
              <TooltipContent side={collapsed ? 'right' : 'top'}>
                <span>{label}</span>
                {helpAction === 'tour' && isDesktop ? (
                  <span className="ml-1 opacity-70">· {hint}</span>
                ) : null}
              </TooltipContent>
            </Tooltip>
          ) : null}

          {showFeedback ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    data-feedback-button="true"
                    aria-label={t('shareFeedback')}
                    className={cn(
                      halfBase,
                      !isSidebarPlacement &&
                        (both
                          ? 'border-border rounded-r-full border-l'
                          : 'rounded-full')
                    )}
                  >
                    <MessageSquare
                      className={cn(
                        'shrink-0',
                        isSidebarPlacement
                          ? 'size-3.5'
                          : 'size-5 transition-transform duration-150 group-hover/half:scale-110'
                      )}
                    />
                    {isSidebarPlacement && !collapsed ? (
                      <span className="whitespace-nowrap">
                        {t('feedbackLabel')}
                      </span>
                    ) : null}
                  </button>
                </DialogTrigger>
              </TooltipTrigger>
              <TooltipContent side={collapsed ? 'right' : 'top'}>
                {t('shareFeedbackShort')}
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      </div>

      <DialogContent className="gap-2 rounded-2xl p-2 sm:max-w-lg">
        <DialogHeader className="sr-only">
          <DialogTitle>{t('shareFeedback')}</DialogTitle>
          <DialogDescription>{t('feedbackDescription')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="relative">
            <Textarea
              id="feedback"
              aria-label={t('feedbackLabel')}
              aria-describedby={
                submitError
                  ? 'feedback-session-details feedback-submit-error'
                  : 'feedback-session-details'
              }
              placeholder={t('feedbackPlaceholder')}
              required
              autoFocus
              disabled={isSubmitting}
              value={feedback}
              onChange={(event) => {
                setFeedback(event.target.value);
                if (submitError) setSubmitError(null);
              }}
              className="border-border/35 focus-visible:border-border/55 min-h-32 resize-none px-4 pt-4 pr-24 pb-10 text-base"
            />
            <span
              aria-hidden="true"
              className="text-muted-foreground bg-background pointer-events-none absolute right-2 bottom-2 rounded px-2 py-0.5 text-xs"
            >
              {t('feedbackLabel')}
            </span>
          </div>

          {submitError ? (
            <p
              id="feedback-submit-error"
              role="alert"
              className="text-destructive px-1 text-xs text-pretty"
            >
              {submitError}
            </p>
          ) : null}

          <div className="flex w-full items-center justify-between gap-3">
            <div
              id="feedback-session-details"
              className="text-muted-foreground border-border/35 bg-muted/30 flex min-w-0 items-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px]"
            >
              <ShieldCheck aria-hidden="true" className="size-3.5 shrink-0" />
              <span className="line-clamp-2 text-pretty">
                {t('feedbackSessionDetails')}
              </span>
            </div>
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting || !feedback.trim()}
              className="shrink-0"
            >
              {isSubmitting ? (
                <>
                  <Loader aria-label={t('submitting')} />
                  {t('submitting')}
                </>
              ) : (
                t('submitFeedback')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
